import type { ImportantContactBodyDtos } from '@lmc/ts-common'
import {
  ImportantContactType,
  type ResidentBody,
  type ResidentItem,
  roleOptions
} from '@lmc/ts-common'
import { FormItem, Input, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import type { UpdateResidentReturnType } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BaseContactForm, { type BaseFormType } from './BaseContactForm'
import { peelImportantContactFormFields } from './peelImportantContactFormFields'
import getOptionsWithOneMoreValue from './utils/getOptionsWithOneMoreValue'

export type FormType = BaseFormType &
  Pick<ImportantContactBodyDtos, 'organisation' | 'speciality'>

type PersonalContactFormProps = {
  onSubmit: (values: Partial<ResidentBody>) => Promise<UpdateResidentReturnType>
  resident: ResidentItem | undefined
  isLoading: boolean
  editedIndex: number | undefined
  testId?: string
}

const PersonalContactForm = ({
  onSubmit,
  resident,
  isLoading,
  editedIndex,
  testId
}: PersonalContactFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const getNewContact = (values: FormType): ImportantContactBodyDtos => {
    const { firstName, lastName, rest } = peelImportantContactFormFields(
      values as unknown as Record<string, unknown>
    )
    return {
      ...rest,
      dependantDetails: undefined,
      isDependant: false,
      lpa: false,
      name: { first: firstName ?? '', last: lastName ?? '' },
      type: ImportantContactType.professional
    } as unknown as ImportantContactBodyDtos
  }

  const residentName =
    resident?.name?.first &&
    resident?.name.last &&
    `${resident.name.first} ${resident.name.last}'s`

  const editedContact =
    editedIndex !== undefined
      ? resident?.importantContacts[editedIndex]
      : undefined

  const options = useMemo(
    () => getOptionsWithOneMoreValue(roleOptions, editedContact?.relationship),
    [editedContact?.relationship]
  )
  const sortedOptions = options.sort((a, b) => (a.label > b.label ? 1 : -1))

  return (
    <BaseContactForm<FormType>
      onSubmit={onSubmit}
      resident={resident}
      editedIndex={editedIndex}
      getNewContact={getNewContact}
      isLoading={isLoading}
      title={`${residentName} ${t(supConstants.PROFESSIONAL_CONTACT)}`}
      testId={testId}
    >
      <FormItem label={t(supConstants.ROLE)} name="relationship">
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          showSearch
          options={sortedOptions}
          optionFilterProp="label"
          data-testid={
            testId
              ? `${testId}-professional-contact-role-select`
              : 'professional-contact-role-select'
          }
        />
      </FormItem>
      <FormItem label={t(supConstants.ORGANISATION)} name="organisation">
        <Input
          data-testid={
            testId
              ? `${testId}-professional-contact-organisation`
              : 'professional-contact-organisation'
          }
        />
      </FormItem>
      <FormItem label={t(supConstants.SPECIALITY)} name="speciality">
        <Input
          data-testid={
            testId
              ? `${testId}-professional-contact-speciality`
              : 'professional-contact-speciality'
          }
        />
      </FormItem>
    </BaseContactForm>
  )
}

export default PersonalContactForm
